import {
  CheckCircleFilled,
  CalendarOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  PlusOutlined,
  SaveOutlined,
  SnippetsOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  message,
  Modal,
  Progress,
  Select,
  Tooltip,
} from "antd";
import zhCN from "antd/lib/date-picker/locale/zh_CN";
import { FC, useEffect, useRef, useState } from "react";
import moment, { Moment } from "moment";
import TaskPriorityTag, { TaskPriority } from "../TaskPriorityTag";
import {
  copyTaskTemplate,
  readTaskTemplate,
  TaskContentClipboardTemplate,
} from "../taskTemplateClipboard";
import "./index.css";

export interface SubtaskContentItem {
  item_id: string;
  content: string;
  completed: boolean;
  start_time: number;
  due_time: number;
  priority: TaskPriority;
  assignees: string[];
  created_at: number;
  updated_at: number;
}

interface SubtaskContentChecklistModalProps {
  canManageContentStatus?: boolean;
  currentUserId: string;
  defaultAssignee?: string;
  getMemberName: (userId: string) => string;
  items: SubtaskContentItem[];
  memberOptions: Array<{
    label: string;
    value: string;
    isProjectMember?: boolean;
  }>;
  onChange: (items: SubtaskContentItem[]) => void;
  onClose: () => void;
  open: boolean;
}

function hasMeaningfulContent(content: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = content || "";
  return (
    Boolean(wrapper.querySelector("img")) ||
    Boolean((wrapper.textContent || "").replace(/\s+/g, " ").trim())
  );
}

function getContentSummary(content: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = content || "";
  return (
    (wrapper.textContent || "").replace(/\s+/g, " ").trim() ||
    (wrapper.querySelector("img") ? "图片内容" : "未填写内容")
  );
}

function getDueTimeText(dueTime: number) {
  return moment(dueTime).format("YYYY年MM月DD日");
}

function getOverdueDays(dueTime: number) {
  return Math.max(1, moment().startOf("day").diff(moment(dueTime).startOf("day"), "days"));
}

function isOverdueItem(item: SubtaskContentItem) {
  return !item.completed && item.due_time < Date.now();
}

function getGroupedMemberOptions(
  options: SubtaskContentChecklistModalProps["memberOptions"]
) {
  const mapOption = (option: SubtaskContentChecklistModalProps["memberOptions"][number]) => ({
    value: option.value,
    searchText: `${option.label} ${option.value}`,
    selectedLabel: option.label,
    label: (
      <span className="subtaskContentChecklistModal__memberOption">
        <i className={option.isProjectMember === false ? "is-team" : "is-project"}>
          {(option.label.trim()[0] || option.value.trim()[0] || "?").toUpperCase()}
        </i>
        <span className="subtaskContentChecklistModal__memberIdentity">
          <strong>{option.label}</strong>
          <small>{option.value}</small>
        </span>
        <em className={option.isProjectMember === false ? "is-team" : "is-project"}>
          {option.isProjectMember === false ? "选择后自动加入" : "项目内"}
        </em>
      </span>
    ),
  });
  const projectMembers = options.filter((option) => option.isProjectMember !== false);
  const teamMembers = options.filter((option) => option.isProjectMember === false);
  return [
    ...(projectMembers.length
      ? [{ label: "项目成员 · 优先显示", options: projectMembers.map(mapOption) }]
      : []),
    ...(teamMembers.length
      ? [{ label: "其他团队成员 · 选择后自动加入项目", options: teamMembers.map(mapOption) }]
      : []),
  ];
}

const SubtaskContentChecklistModal: FC<
  SubtaskContentChecklistModalProps
> = ({
  canManageContentStatus = false,
  currentUserId,
  defaultAssignee,
  getMemberName,
  items,
  memberOptions,
  onChange,
  onClose,
  open,
}) => {
  const [draftItems, setDraftItems] = useState<SubtaskContentItem[]>([]);
  const [editingItemId, setEditingItemId] = useState("");
  const [editingContent, setEditingContent] = useState("");
  const [editingStartTime, setEditingStartTime] = useState<Moment | null>(null);
  const [editingDueTime, setEditingDueTime] = useState<Moment | null>(null);
  const [editingPriority, setEditingPriority] =
    useState<TaskPriority>("medium");
  const [editingAssignees, setEditingAssignees] = useState<string[]>([]);
  const editorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftItems(items.map((item) => ({ ...item })));
    setEditingItemId("");
    setEditingContent("");
    setEditingStartTime(null);
    setEditingDueTime(null);
    setEditingPriority("medium");
    setEditingAssignees([]);
  }, [items, open]);

  const completedCount = draftItems.filter((item) => item.completed).length;
  const progress = draftItems.length
    ? Math.round((completedCount / draftItems.length) * 100)
    : 0;

  const beginCreate = () => {
    setEditingItemId("new");
    setEditingContent("");
    setEditingStartTime(moment().startOf("day"));
    setEditingDueTime(null);
    setEditingPriority("medium");
    const fallbackAssignee = memberOptions.some(
      (option) => option.value === defaultAssignee
    )
      ? defaultAssignee || ""
      : memberOptions[0]?.value || "";
    setEditingAssignees(fallbackAssignee ? [fallbackAssignee] : []);
    window.requestAnimationFrame(() => {
      if (editorRef.current) editorRef.current.innerHTML = "";
    });
  };

  const beginEdit = (item: SubtaskContentItem) => {
    setEditingItemId(item.item_id);
    setEditingContent(item.content);
    setEditingStartTime(moment(item.start_time));
    setEditingDueTime(moment(item.due_time));
    setEditingPriority(item.priority);
    setEditingAssignees([...item.assignees]);
    window.requestAnimationFrame(() => {
      if (editorRef.current) editorRef.current.innerHTML = item.content;
    });
  };

  const copyContentItem = async (item: SubtaskContentItem) => {
    await copyTaskTemplate<TaskContentClipboardTemplate>("content-item", {
      content: item.content,
      start_time: item.start_time,
      due_time: item.due_time,
      priority: item.priority,
      assignees: [...item.assignees],
    });
    message.success("任务内容已复制，可在新增内容中粘贴");
  };

  const pasteContentItem = async () => {
    const template = await readTaskTemplate<TaskContentClipboardTemplate>(
      "content-item"
    );
    if (!template) {
      message.warning("剪贴板中没有可用的任务内容数据");
      return;
    }
    setEditingContent(template.content || "");
    setEditingStartTime(
      template.start_time ? moment(template.start_time) : moment().startOf("day")
    );
    setEditingDueTime(template.due_time ? moment(template.due_time) : null);
    setEditingPriority(template.priority || "medium");
    const availableMembers = new Set(memberOptions.map((option) => option.value));
    const copiedAssignees = (template.assignees || []).filter((id) =>
      availableMembers.has(id)
    );
    const fallbackAssignee = availableMembers.has(defaultAssignee || "")
      ? defaultAssignee || ""
      : memberOptions[0]?.value || "";
    setEditingAssignees(
      copiedAssignees.length
        ? copiedAssignees
        : fallbackAssignee
        ? [fallbackAssignee]
        : []
    );
    window.requestAnimationFrame(() => {
      if (editorRef.current) editorRef.current.innerHTML = template.content || "";
    });
    message.success("已粘贴任务内容，完成状态已重置");
  };

  const saveCurrentItem = () => {
    const content = (editorRef.current?.innerHTML || editingContent).trim();
    if (!hasMeaningfulContent(content)) {
      message.warning("请输入任务内容");
      return;
    }
    if (!editingStartTime || !editingDueTime) {
      message.warning("请选择预期开始时间和预期完成时间");
      return;
    }
    if (!editingAssignees.length) {
      message.warning("请至少选择一名任务内容负责人");
      return;
    }
    const now = Date.now();
    const start_time = editingStartTime.clone().startOf("day").valueOf();
    const due_time = editingDueTime.clone().endOf("day").valueOf();
    if (start_time > due_time) {
      message.warning("预期开始时间不能晚于预期完成时间");
      return;
    }
    if (editingItemId === "new") {
      setDraftItems((current) => [
        ...current,
        {
          item_id: `draft-content-${now}-${current.length}`,
          content,
          completed: false,
          start_time,
          due_time,
          priority: editingPriority,
          assignees: [...editingAssignees],
          created_at: now,
          updated_at: now,
        },
      ]);
    } else {
      setDraftItems((current) =>
        current.map((item) =>
          item.item_id === editingItemId
            ? {
                ...item,
                content,
                start_time,
                due_time,
                priority: editingPriority,
                assignees: [...editingAssignees],
                updated_at: now,
              }
            : item
        )
      );
    }
    setEditingItemId("");
    setEditingContent("");
    setEditingStartTime(null);
    setEditingDueTime(null);
    setEditingPriority("medium");
    setEditingAssignees([]);
  };

  const deleteItem = (item: SubtaskContentItem) => {
    Modal.confirm({
      centered: true,
      className: "subtaskContentChecklistConfirm",
      title: "删除任务内容",
      content: `确认删除“${getContentSummary(item.content)}”吗？`,
      okButtonProps: { danger: true },
      okText: "确认删除",
      cancelText: "取消",
      onOk: () => {
        setDraftItems((current) =>
          current.filter((currentItem) => currentItem.item_id !== item.item_id)
        );
        if (editingItemId === item.item_id) {
          setEditingItemId("");
          setEditingContent("");
          setEditingStartTime(null);
          setEditingDueTime(null);
          setEditingPriority("medium");
          setEditingAssignees([]);
        }
      },
    });
  };

  const toggleItem = (itemId: string, completed: boolean) => {
    const item = draftItems.find((current) => current.item_id === itemId);
    if (
      !canManageContentStatus &&
      !item?.assignees.includes(currentUserId)
    ) {
      message.warning("只有该内容项负责人或管理员可以修改完成状态");
      return;
    }
    setDraftItems((current) =>
      current.map((item) =>
        item.item_id === itemId
          ? { ...item, completed, updated_at: Date.now() }
          : item
      )
    );
  };

  const appendImage = (src: string) => {
    if (!editorRef.current) return;
    const image = document.createElement("img");
    image.src = src;
    image.alt = "任务内容图片";
    editorRef.current.appendChild(image);
    editorRef.current.appendChild(document.createElement("br"));
    setEditingContent(editorRef.current.innerHTML);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const imageFiles = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/")
    );
    if (!imageFiles.length) return;
    event.preventDefault();
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => appendImage(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  };

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      centered
      className="subtaskContentChecklistModal"
      closable={false}
      destroyOnClose
      footer={null}
      onCancel={onClose}
      open={open}
      title={null}
      width={1040}
    >
      <div className="subtaskContentChecklistModal__shell">
        <header>
          <div className="subtaskContentChecklistModal__identity">
            <span className="subtaskContentChecklistModal__identityIcon">
              <FileTextOutlined />
            </span>
            <div>
              <h3>任务内容清单</h3>
              <span>{draftItems.length} 项内容</span>
            </div>
          </div>
          <div className="subtaskContentChecklistModal__progressSummary">
            <Progress
              percent={progress}
              showInfo={false}
              strokeColor="#34d399"
              trailColor="#243349"
              type="circle"
              width={48}
            />
            <div>
              <strong>{progress}%</strong>
              <span>{completedCount} / {draftItems.length} 已完成</span>
            </div>
          </div>
          <Button
            aria-label="关闭任务内容弹窗"
            icon={<CloseOutlined />}
            onClick={onClose}
          />
        </header>

        <main className="subtaskContentChecklistModal__body">
          <section className="subtaskContentChecklistModal__listPane">
            <div className="subtaskContentChecklistModal__listToolbar">
              <div>
                <strong>内容项</strong>
                <span>
                  共 {draftItems.length} 项 · {completedCount} 项已完成
                </span>
              </div>
              <Button
                className="subtaskContentChecklistModal__createButton"
                icon={<PlusOutlined />}
                onClick={beginCreate}
                type="primary"
              >
                新增内容
              </Button>
            </div>
            <div className="subtaskContentChecklistModal__list">
              {draftItems.map((item, index) => (
                <article
                  className={`${item.completed ? "is-completed" : ""} ${
                    isOverdueItem(item) ? "is-overdue" : ""
                  } ${
                    editingItemId === item.item_id ? "is-active" : ""
                  }`}
                  key={item.item_id}
                  onClick={() => beginEdit(item)}
                >
                  <Checkbox
                    aria-label={`切换任务内容 ${index + 1} 完成状态`}
                    checked={item.completed}
                    disabled={
                      !canManageContentStatus &&
                      !item.assignees.includes(currentUserId)
                    }
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => toggleItem(item.item_id, event.target.checked)}
                  />
                  <div>
                    <span>内容项 {String(index + 1).padStart(2, "0")}</span>
                    <strong title={getContentSummary(item.content)}>
                      {getContentSummary(item.content)}
                    </strong>
                    <div className="subtaskContentChecklistModal__itemMeta">
                      <small className={isOverdueItem(item) ? "is-overdue" : ""}>
                        <CalendarOutlined />
                        {getDueTimeText(item.start_time)} 至{" "}
                        {getDueTimeText(item.due_time)}
                        {isOverdueItem(item)
                          ? ` · 已逾期 ${getOverdueDays(item.due_time)} 天`
                          : ""}
                      </small>
                      <TaskPriorityTag priority={item.priority} />
                    </div>
                    <div className="subtaskContentChecklistModal__itemAssignees">
                      <UserOutlined />
                      {item.assignees.map((assignee) => (
                        <span key={assignee}>{getMemberName(assignee)}</span>
                      ))}
                    </div>
                  </div>
                  <span
                    className={`subtaskContentChecklistModal__itemState ${
                      item.completed ? "is-done" : "is-todo"
                    }`}
                  >
                    {item.completed ? (
                      <>
                        <CheckCircleFilled /> 已完成
                      </>
                    ) : (
                      <>
                        <ClockCircleOutlined /> 待完成
                      </>
                    )}
                  </span>
                  <div className="subtaskContentChecklistModal__itemActions">
                    <Tooltip title="复制内容项">
                      <Button
                        aria-label={`复制任务内容 ${index + 1}`}
                        icon={<CopyOutlined />}
                        onClick={(event) => {
                          event.stopPropagation();
                          copyContentItem(item);
                        }}
                      />
                    </Tooltip>
                    <Button
                      aria-label={`编辑任务内容 ${index + 1}`}
                      icon={<EditOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        beginEdit(item);
                      }}
                    />
                    <Button
                      aria-label={`删除任务内容 ${index + 1}`}
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteItem(item);
                      }}
                    />
                  </div>
                </article>
              ))}
              {!draftItems.length && (
                <div className="subtaskContentChecklistModal__empty">
                  <FileTextOutlined />
                  <strong>暂无任务内容</strong>
                  <span>至少新增一项后才能保存子任务</span>
                </div>
              )}
            </div>
          </section>

          <section className="subtaskContentChecklistModal__editorPane">
            {editingItemId ? (
              <>
                <div className="subtaskContentChecklistModal__editorHeader">
                <div>
                  <span className="subtaskContentChecklistModal__editorKicker">
                    {editingItemId === "new"
                      ? "新建内容项"
                      : `内容项 ${String(
                          draftItems.findIndex(
                            (item) => item.item_id === editingItemId
                          ) + 1
                        ).padStart(2, "0")}`}
                  </span>
                  <strong className="subtaskContentChecklistModal__editorTitle">
                    {editingItemId === "new" ? "新增任务内容" : "编辑任务内容"}
                  </strong>
                </div>
                <div>
                  {editingItemId === "new" && (
                    <Button
                      aria-label="粘贴内容"
                      className="subtaskContentChecklistModal__pasteContent"
                      icon={<SnippetsOutlined />}
                      onClick={pasteContentItem}
                    >
                      粘贴内容
                    </Button>
                  )}
                  <Button
                    className="subtaskContentChecklistModal__cancelEdit"
                    onClick={() => {
                      setEditingItemId("");
                      setEditingContent("");
                      setEditingStartTime(null);
                      setEditingDueTime(null);
                      setEditingPriority("medium");
                      setEditingAssignees([]);
                    }}
                  >
                    取消
                  </Button>
                  <Button
                    className="subtaskContentChecklistModal__saveContent"
                    icon={<SaveOutlined />}
                    onClick={saveCurrentItem}
                    type="primary"
                  >
                    保存内容
                  </Button>
                </div>
              </div>
              <div className="subtaskContentChecklistModal__scheduleFields">
                <label className="subtaskContentChecklistModal__assigneeField">
                  <span>任务内容负责人 <b>必填 · 可多选</b></span>
                  <Select
                    aria-label="任务内容负责人"
                    maxTagCount={4}
                    maxTagPlaceholder={(omittedValues) =>
                      `+${omittedValues.length} 位负责人`
                    }
                    mode="multiple"
                    onChange={setEditingAssignees}
                    dropdownMatchSelectWidth={400}
                    listHeight={312}
                    notFoundContent="没有匹配的成员"
                    optionFilterProp="searchText"
                    optionLabelProp="selectedLabel"
                    options={getGroupedMemberOptions(memberOptions)}
                    placeholder="选择负责该内容项的成员"
                    popupClassName="subtaskContentAssigneeDropdown"
                    showSearch
                    value={editingAssignees}
                    virtual={false}
                  />
                </label>
                <label>
                  <span>预期开始时间 <b>必填</b></span>
                  <DatePicker
                    allowClear={false}
                    aria-label="任务内容预期开始时间"
                    format="YYYY年MM月DD日"
                    locale={zhCN}
                    onChange={(value) => setEditingStartTime(value)}
                    placeholder="请选择开始时间"
                    popupClassName="subtaskContentChecklistDatePicker"
                    suffixIcon={<CalendarOutlined />}
                    value={editingStartTime}
                  />
                </label>
                <label>
                  <span>预期完成时间 <b>必填</b></span>
                  <DatePicker
                    allowClear={false}
                    aria-label="任务内容预期完成时间"
                    format="YYYY年MM月DD日"
                    locale={zhCN}
                    onChange={(value) => setEditingDueTime(value)}
                    placeholder="请选择完成时间"
                    popupClassName="subtaskContentChecklistDatePicker"
                    suffixIcon={<CalendarOutlined />}
                    value={editingDueTime}
                  />
                </label>
                <label>
                  <span>优先级 <b>必填</b></span>
                  <Select
                    aria-label="任务内容优先级"
                    onChange={setEditingPriority}
                    options={[
                      { label: "低优先级", value: "low" },
                      { label: "中优先级", value: "medium" },
                      { label: "高优先级", value: "high" },
                      { label: "紧急", value: "urgent" },
                    ]}
                    value={editingPriority}
                  />
                </label>
              </div>
              <div
                aria-label="任务内容编辑器"
                className="subtaskContentChecklistModal__richEditor"
                contentEditable
                data-placeholder="输入任务内容，可直接粘贴图片"
                onInput={(event) => setEditingContent(event.currentTarget.innerHTML)}
                onPaste={handlePaste}
                ref={editorRef}
                suppressContentEditableWarning
              />
              </>
            ) : (
              <div className="subtaskContentChecklistModal__editorEmpty">
                <span className="subtaskContentChecklistModal__editorEmptyIcon">
                  <EditOutlined />
                </span>
                <strong>选择内容项进行编辑</strong>
                <span>或新建一项任务内容</span>
                <Button
                  className="subtaskContentChecklistModal__createFromEmpty"
                  icon={<PlusOutlined />}
                  onClick={beginCreate}
                  type="primary"
                >
                  新增内容
                </Button>
              </div>
            )}
          </section>
        </main>

        <footer>
          <span>
            {canManageContentStatus
              ? "管理员可修改全部内容项完成状态"
              : "仅对应内容项负责人可修改完成状态"}
          </span>
          <div>
            <Button onClick={onClose}>取消</Button>
            <Button
              disabled={!draftItems.length || Boolean(editingItemId)}
              icon={<SaveOutlined />}
              onClick={() => {
                onChange(draftItems);
                onClose();
              }}
              type="primary"
            >
              保存清单
            </Button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

export default SubtaskContentChecklistModal;
